import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Zap, Search, RefreshCw, Sparkles, Loader, AlertTriangle, X } from 'lucide-react';
import { generateTradeSignals, getBatchQuotes, getLastFilterStats, applyCalibrationCurve } from '../utils/marketEngine';
import { validateSignalsBatch } from '../utils/validateSignalsBatch';
import { apiEvaluateSignals, apiGetCalibrationCurvePublic } from '../utils/backendApi';
import { TERMS } from '../utils/terms';
import { useExchange } from '../context/ExchangeContext';
import SignalCard from '../components/SignalCard';
import ErrorBoundary from '../components/ErrorBoundary';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import CountUp from '../components/ui/CountUp';

// Median signal-load time observed in production. The fetch-phase progress bar
// fills proportionally toward this target; past it, growth slows asymptotically
// so the bar never hits 100% until the actual load completes.
const FETCH_TARGET_MS = 12_000;

// ── Loading / error UI ───────────────────────────────────────────────────────
function ProgressBanner({ phase, elapsedMs, validateDone, validateTotal }) {
  // phase: 'fetch' | 'validate' | null
  if (!phase) return null;

  const isFetch = phase === 'fetch';
  // Asymptotic approach to 100% during fetch: pct = 1 - exp(-t / target).
  // At target time we're at ~63%, at 2× target ~86% — honest that we don't
  // actually know when it'll finish without crossing the finish line.
  const fetchPct     = Math.min(95, (1 - Math.exp(-elapsedMs / FETCH_TARGET_MS)) * 100);
  const validatePct  = validateTotal > 0 ? (validateDone / validateTotal) * 100 : 0;
  const pct          = isFetch ? fetchPct : validatePct;
  const elapsedSec   = Math.floor(elapsedMs / 1000);
  const label        = isFetch
    ? `Scanning the market for signals${elapsedSec > 0 ? ` · ${elapsedSec}s` : ''}`
    : `Backtesting ${validateDone}/${validateTotal} signals against the last 90 days`;
  const sublabel     = isFetch
    ? 'Pulling top movers, technicals, news, and options chains. Usually 8-15 seconds.'
    : 'Each card flips from VALIDATING to VALIDATED as its backtest completes.';
  const barColor     = isFetch ? '#6366f1' : '#10b981';

  return (
    <div style={{
      marginBottom: 16, padding: '12px 14px', borderRadius: 10,
      background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(51,65,85,0.45)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Loader size={13} color={barColor} style={{ animation: 'spin 1.2s linear infinite' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{label}</span>
        <span style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: '#64748b' }}>{Math.round(pct)}%</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'rgba(30,41,59,0.7)', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`, background: barColor,
          borderRadius: 2, transition: 'width 0.3s ease-out',
        }} />
      </div>
      <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>{sublabel}</div>
    </div>
  );
}

// Compact "where did the candidates go" breakdown. Shown as a footer line
// whenever a scan completes (filterStats.timestamp > 0); the empty state
// promotes it to a prominent block so the user can see the dominant drop
// reason at a glance.
function describeFilterStats(stats) {
  if (!stats) return null;
  const dropEntries = [
    { key: 'earningsBlackout', label: 'earnings blackout' },
    { key: 'earningsNews',     label: 'earnings news' },
    { key: 'confidenceLow',    label: 'low confidence' },
    { key: 'scoreLow',         label: 'low score' },
    { key: 'hold',             label: 'no clear direction' },
    { key: 'regimeFiltered',   label: 'regime filter (bear LONG)' },
    { key: 'counterTrendLong', label: 'counter-trend LONG' },
    { key: 'rrLow',            label: 'R:R below floor' },
    { key: 'exception',        label: 'fetch errors' },
  ];
  const drops = dropEntries
    .map(({ key, label }) => ({ key, label, n: stats.drops?.[key] || 0 }))
    .filter(d => d.n > 0)
    .sort((a, b) => b.n - a.n);
  return { drops, ...stats };
}

function ScanFootnote({ stats }) {
  const d = describeFilterStats(stats);
  if (!d || !d.timestamp) return null;
  const rawUniverse = Number(d.universeRaw ?? d.universeSize ?? 0);
  const universe   = Number(d.universeSize ?? 0);
  const candidates = Number(d.candidatePool ?? 0);
  const shipped    = Number(d.shipped ?? 0);
  // Show "raw → liquid" only when both numbers exist and differ meaningfully.
  // Polygon's grouped-daily returns the full US universe (~8-9k rows); the
  // liquidity floor (price ≥ $5, vol ≥ 500k) trims to ~3k actually tradeable
  // names. Surfacing both prevents the "why only 3k?" confusion.
  const showRaw = rawUniverse > 0 && rawUniverse > universe + 100 && !d.fallback;
  const drops      = Array.isArray(d.drops) ? d.drops : [];
  const dropSummary = drops.length === 0
    ? 'all candidates shipped'
    : drops.map(x => `${x.n} ${x.label}`).join(' · ');
  const ageSec = Math.max(0, Math.round((Date.now() - (d.timestamp || Date.now())) / 1000));
  const universeLabel = d.fallback ? 'curated fallback' : 'stocks';

  // VIX badge — shows the current regime so the user can verify which gate
  // floors are actually being applied. Colour-coded: stressed = amber,
  // calm = green, normal = neutral, unknown = grey (failed fetch).
  const regime  = d.vixRegime || 'unknown';
  const gates   = d.gates;
  const regimeColor = regime === 'stressed' ? '#f59e0b'
                    : regime === 'calm'     ? '#10b981'
                    : regime === 'normal'   ? '#94a3b8'
                    : '#475569';
  const regimeTitle = regime === 'unknown'
    ? 'No VIX data available'
    : `VIX ${d.vixLevel != null ? d.vixLevel.toFixed(1) : 'n/a'} → regime '${regime}'. Active gates: score ≥ ${gates?.minScore}, confidence ≥ ${gates?.minConfidence}%, counter-trend LONG override score ≥ ${gates?.longCounterTrendScoreMin}. Bands: VIX <18 = calm, 18-25 = normal, ≥25 = stressed (tightened).`;

  return (
    <div style={{
      fontSize: 10, color: '#64748b', marginTop: 14, padding: '6px 4px',
      borderTop: '1px solid rgba(51,65,85,0.25)',
      display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
    }}>
      {d.fallback && (
        <span
          title={`Bulk scan failed: ${d.fallbackReason || 'unknown'} — using curated list instead`}
          style={{ color: '#f59e0b', fontWeight: 600 }}
        >
          ⚠ fallback scan
        </span>
      )}
      {regime !== 'unknown' && (
        <span
          title={regimeTitle}
          style={{
            color: regimeColor,
            border: `1px solid ${regimeColor}33`,
            background: `${regimeColor}11`,
            borderRadius: 4,
            padding: '1px 6px',
            fontWeight: 600,
          }}
        >
          VIX {d.vixLevel != null ? d.vixLevel.toFixed(1) : '—'} · {regime}
        </span>
      )}
      {showRaw ? (
        <>
          <span title="Polygon's grouped-daily endpoint returns every US-listed stock, ETF and ADR.">
            Polygon <b style={{ color: '#94a3b8' }}>{rawUniverse.toLocaleString()}</b>
          </span>
          <span title="Names that pass the liquidity floor: price ≥ $5 AND daily volume ≥ 500k AND open > 0. Penny stocks, halted names, and illiquid tickers drop here.">
            → <b style={{ color: '#94a3b8' }}>{universe.toLocaleString()}</b> liquid
          </span>
        </>
      ) : (
        <span>Scanned <b style={{ color: '#94a3b8' }}>{universe.toLocaleString()}</b> {universeLabel}</span>
      )}
      <span>→ <b style={{ color: '#94a3b8' }}>{candidates}</b> top-momentum</span>
      <span>→ <b style={{ color: '#10b981' }}>{shipped}</b> signals shipped</span>
      {drops.length > 0 && <span style={{ color: '#475569' }}>· {dropSummary}</span>}
      <span style={{ marginLeft: 'auto', color: '#475569' }}>{ageSec}s ago</span>
    </div>
  );
}

function FilterBreakdownEmpty({ stats, onRetry }) {
  const d = describeFilterStats(stats);
  if (!d || !d.timestamp) return null;
  const universe   = Number(d.universeSize ?? 0);
  const candidates = Number(d.candidatePool ?? 0);
  const drops      = Array.isArray(d.drops) ? d.drops : [];
  return (
    <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
      <Zap size={36} style={{ marginBottom: 10, opacity: 0.3 }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: '#cbd5e1' }}>
        No signals passed the quality gates
      </div>
      <div style={{ fontSize: 12, marginTop: 4, marginBottom: 16, color: '#64748b' }}>
        Scanned <b>{universe.toLocaleString()}</b> stocks, ranked the top <b>{candidates}</b> by momentum, but every one was filtered:
      </div>
      <div style={{
        display: 'inline-block', textAlign: 'left',
        background: 'rgba(15,23,42,0.6)', borderRadius: 10,
        border: '1px solid rgba(51,65,85,0.4)', padding: '12px 18px',
        minWidth: 260,
      }}>
        {drops.length === 0
          ? <div style={{ fontSize: 12, color: '#64748b' }}>No filter accounted for the drop — likely a data-fetch failure upstream.</div>
          : drops.map(x => (
              <div key={x.key} style={{
                display: 'flex', justifyContent: 'space-between', gap: 16,
                fontSize: 12, padding: '3px 0',
              }}>
                <span style={{ color: '#cbd5e1' }}>{x.label}</span>
                <span className="mono" style={{ color: '#94a3b8', fontWeight: 600 }}>{x.n}</span>
              </div>
            ))
        }
      </div>
      {onRetry && (
        <div style={{ marginTop: 18 }}>
          <button
            onClick={onRetry}
            style={{
              padding: '8px 18px', borderRadius: 7,
              border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.08)',
              color: '#818cf8', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <RefreshCw size={12} />
            Try again
          </button>
        </div>
      )}
    </div>
  );
}

function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{
      marginBottom: 16, padding: '12px 14px', borderRadius: 10,
      background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)',
      display: 'flex', alignItems: 'flex-start', gap: 10,
    }}>
      <AlertTriangle size={14} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#ef4444', marginBottom: 2 }}>
          Couldn&apos;t load signals
        </div>
        <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5, wordBreak: 'break-word' }}>
          {message}
        </div>
        <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>
          Hit &quot;Refresh Signals&quot; to retry, or wait — the page auto-refreshes every 5 minutes.
        </div>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss error"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#64748b', padding: 2, flexShrink: 0,
          }}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

export default function SignalsPage() {
  const { setSelectedTicker, openTrade } = useOutletContext();
  const navigate = useNavigate();
  const { config } = useExchange();
  const [signals, setSignals] = useState([]);
  const [liveQuotes, setLiveQuotes] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Validation progress for the in-flight batch backtest. validateTotal = 0
  // means "no active validation".
  const [validateDone, setValidateDone]   = useState(0);
  const [validateTotal, setValidateTotal] = useState(0);
  const [aiInsights, setAiInsights] = useState({});   // { ticker: { adjustment, reasoning } }
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  // Hard failure of the signal-loading pipeline — surfaces in the error banner.
  const [loadError, setLoadError] = useState(null);
  // Elapsed-ms ticker so the fetch-phase progress bar can update visibly while
  // generateTradeSignals is in flight (it doesn't expose progress events).
  const [loadingElapsedMs, setLoadingElapsedMs] = useState(0);
  const loadStartRef = useRef(0);
  // Per-scan filter breakdown — populated after each loadSignals settles.
  // Drives the footer ("scanned 8k → 23 → 3 …") and the empty-state explanation
  // (which gate caught every candidate).
  const [filterStats, setFilterStats] = useState(null);

  // Cancel-flag for the in-flight batch validation. Each new loadSignals() call
  // flips the previous one's flag so progressive updates from a stale validation
  // pass don't write into the new signal set.
  const cancelValidationRef = useRef(null);

  // T1.3: reliability curve fetched once per page mount. Cached on a ref so
  // validation callbacks read it synchronously without re-renders. Empty array
  // on first load = identity behaviour (matches pre-T1 display). Defensive
  // fetch returns [] on any error so a slow/failing endpoint never blocks
  // render.
  const calibrationCurveRef = useRef([]);
  useEffect(() => {
    apiGetCalibrationCurvePublic().then(curve => { calibrationCurveRef.current = curve; });
  }, []);

  // Drive the elapsed-time counter for the fetch progress bar.
  useEffect(() => {
    if (!refreshing) return;
    const tick = () => setLoadingElapsedMs(Date.now() - loadStartRef.current);
    tick();
    const iv = setInterval(tick, 250);
    return () => clearInterval(iv);
  }, [refreshing]);

  const loadSignals = useCallback(async (force = false) => {
    // Cancel any previous validation pass first.
    if (cancelValidationRef.current) cancelValidationRef.current();

    loadStartRef.current = Date.now();
    setLoadingElapsedMs(0);
    setRefreshing(true);
    setLoadError(null);
    setAiInsights({});
    setAiSummary(null);
    setAiError(null);
    setValidateDone(0);
    setValidateTotal(0);
    try {
      // Initial page = 50 signals. Polygon has no rate limit so a larger
      // first page costs nothing extra and gives users a fuller picture before
      // they have to "Load More" (which pulls 25 more at a time below).
      //
      // We fetch the calibration curve in parallel and await BOTH before
      // proceeding — without this, fast-cached backtest callbacks can fire
      // while calibrationCurveRef is still [] and applyCalibrationCurve
      // returns the raw heuristic instead of the observed-WR mapping.
      const count = 50;
      const [fresh, curve] = await Promise.all([
        generateTradeSignals(count, 'NYSE', force),
        apiGetCalibrationCurvePublic(),
      ]);
      calibrationCurveRef.current = curve;
      // Pull the per-scan filter breakdown immediately after the call so the
      // footer / empty-state can show where every candidate went.
      setFilterStats(getLastFilterStats());
      // generateTradeSignals returns sorted by raw score; re-sort by confidence
      // DESC (score DESC as tiebreaker) so the highest-conviction setups land
      // at the top — same comparator used by Load More and the filtered view.
      fresh.sort((a, b) => {
        const conf = (b.confidence ?? 0) - (a.confidence ?? 0);
        if (conf !== 0) return conf;
        return (b.score ?? 0) - (a.score ?? 0);
      });
      // Tag every directional signal as pending so cards render VALIDATING immediately.
      const tagged = fresh.map(s =>
        s.direction === 'HOLD' ? s : { ...s, _confidenceSource: 'pending' }
      );
      setSignals(tagged);
      setRefreshing(false);

      // Empty fresh result is not necessarily an error — it can legitimately
      // happen during low-momentum sessions or earnings blackouts. We tell the
      // user about it via a softer empty-state below, not the red banner.

      // Auto-validate every signal in parallel. Cards flip one-by-one as each
      // backtest settles. Concurrency capped inside the helper.
      let cancelled = false;
      cancelValidationRef.current = () => { cancelled = true; };
      const directional = tagged.filter(s => s.direction !== 'HOLD');
      setValidateTotal(directional.length);

      validateSignalsBatch(
        tagged,
        ({ ticker, status, result, done }) => {
          if (cancelled) return;
          setSignals(prev => prev.map(s => {
            if (s.ticker !== ticker) return s;
            if (status === 'validated') {
              // The per-ticker backtest (with Sharpe folded in) is more
              // specific evidence than the cluster-level Bayesian shrink, so
              // overwrite BOTH confidence and calibratedConfidence with it.
              // That way the SignalCard renders one unified verdict instead
              // of showing the higher of two unrelated calibrations.
              //
              // T1.3: then remap through the reliability curve so the
              // displayed number is the historically observed WR at that
              // score. Identity-mapped bins leave the value unchanged.
              const preCurve = result.calibratedConfidence;
              // Use the local `curve` from the Promise.all closure, not
              // the ref — guarantees the curve we awaited is what's
              // applied, regardless of any later overwrite.
              const { mapped, bin } = applyCalibrationCurve(preCurve, curve);
              return {
                ...s,
                confidence: mapped,
                calibratedConfidence: mapped,
                preCurveConfidence: preCurve,
                reliabilityBin: bin,
                _confidenceSource: 'backtest',
                _backtestResult: result,
              };
            }
            return { ...s, _confidenceSource: status }; // 'insufficient' | 'error'
          }));
          setValidateDone(done);
        },
        { isCancelled: () => cancelled }
      ).then(() => {
        if (!cancelled) { setValidateTotal(0); setValidateDone(0); }
      });
    } catch (e) {
      console.error('Failed to load signals:', e);
      setRefreshing(false);
      setLoadError(e?.message || 'Unknown error while loading signals.');
    }
  }, []);

  // Cancel any active validation on unmount.
  useEffect(() => () => {
    if (cancelValidationRef.current) cancelValidationRef.current();
  }, []);

  const handleAiAnalysis = useCallback(async () => {
    if (aiLoading || signals.length === 0) return;
    setAiLoading(true);
    setAiError(null);
    setAiSummary(null);
    try {
      const payload = signals.map(s => ({
        ticker:     s.ticker,
        direction:  s.direction,
        confidence: s.confidence,
        changePct:  s.changePct,
        reasons:    s.reasons,
      }));
      const result = await apiEvaluateSignals(payload);
      const map = {};
      for (const a of result.adjustments ?? []) map[a.ticker] = a;
      setAiInsights(map);
      setAiSummary(result.summary ?? null);
    } catch (e) {
      setAiError(e.message || 'AI analysis failed');
    }
    setAiLoading(false);
  }, [signals, aiLoading]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const excludeTickers = signals.map(s => s.ticker);
      // Same curve-race guard as loadSignals — await the curve before
      // validation callbacks can fire on fast-cached backtests.
      const [more, curve] = await Promise.all([
        generateTradeSignals(25, 'NYSE', true, excludeTickers),
        apiGetCalibrationCurvePublic(),
      ]);
      calibrationCurveRef.current = curve;
      setFilterStats(getLastFilterStats());
      if (more.length === 0) {
        // The scanner already retried with an expanded pool; if it still
        // returned zero, the gate stack has exhausted the universe at this
        // VIX regime. Surface the reason via the existing filter-stats
        // footer (which now shows the expanded pool's drop breakdown).
        setLoadError('No new signals found — every remaining candidate failed a gate. Try again later as conditions change.');
        setLoadingMore(false);
        return;
      }
      // Tag fresh directional signals as pending so each new card renders
      // VALIDATING — matches the initial loadSignals contract. Without this,
      // load-more signals stayed at their raw heuristic confidence forever
      // (no backtest, no Sharpe folded in) and could rank above the
      // properly-validated initial batch in the merge sort.
      const tagged = more.map(s =>
        s.direction === 'HOLD' ? s : { ...s, _confidenceSource: 'pending' }
      );
      // Merge the new batch into the existing list, then re-sort the whole
      // list by confidence DESC (score DESC as tiebreaker) so the top of the
      // page always shows the highest-conviction picks regardless of which
      // batch they came from.
      setSignals(prev => [...prev, ...tagged].sort((a, b) => {
        const conf = (b.confidence ?? 0) - (a.confidence ?? 0);
        if (conf !== 0) return conf;
        return (b.score ?? 0) - (a.score ?? 0);
      }));

      // Run the same validation pass on the new batch so each card flips
      // VALIDATING → VALIDATED with its backtest-derived confidence, just
      // like the initial load. We mint a fresh cancel-flag for THIS batch
      // (not the cancelValidationRef used by loadSignals) so a subsequent
      // full reload's cancellation doesn't accidentally kill the load-more
      // validation that's still in-flight, and vice versa.
      let cancelled = false;
      const prevCancel = cancelValidationRef.current;
      cancelValidationRef.current = () => { cancelled = true; if (prevCancel) prevCancel(); };
      const directional = tagged.filter(s => s.direction !== 'HOLD');
      // Reset both counters to track only THIS load-more batch. The prior
      // approach accumulated onto the initial batch's stale validateDone
      // (which was never reset when validateTotal flipped to 0 after the
      // first pass finished), producing nonsense like "Validating 23/8".
      setValidateDone(0);
      setValidateTotal(directional.length);

      validateSignalsBatch(
        tagged,
        ({ ticker, status, result, done }) => {
          if (cancelled) return;
          setSignals(prev => prev.map(s => {
            if (s.ticker !== ticker) return s;
            if (status === 'validated') {
              // T1.3: remap through the reliability curve, same as loadSignals.
              // Use the local `curve` from the Promise.all closure to
              // avoid any race with the shared ref.
              const preCurve = result.calibratedConfidence;
              const { mapped, bin } = applyCalibrationCurve(preCurve, curve);
              return {
                ...s,
                confidence: mapped,
                calibratedConfidence: mapped,
                preCurveConfidence: preCurve,
                reliabilityBin: bin,
                _confidenceSource: 'backtest',
                _backtestResult: result,
              };
            }
            return { ...s, _confidenceSource: status };
          }));
          // Use the `done` index reported by validateSignalsBatch so
          // the counter never drifts past `total` (the prior d+1
          // pattern would compound on top of the initial batch's leftover).
          setValidateDone(done);
        },
        { isCancelled: () => cancelled }
      ).then(() => {
        if (!cancelled) { setValidateTotal(0); setValidateDone(0); }
      });
    } catch (e) {
      console.error('Failed to load more signals:', e);
      setLoadError(e?.message || 'Failed to load more signals.');
    }
    setLoadingMore(false);
  }, [signals]);

  useEffect(() => { loadSignals(false); }, [loadSignals]);
  useEffect(() => {
    const iv = setInterval(() => loadSignals(true), 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [loadSignals]);

  // Refresh live prices for signal cards every 30 seconds so displayed price and trade price are current
  useEffect(() => {
    if (signals.length === 0) return;
    const tickers = signals.map(s => s.ticker);
    const refresh = () => getBatchQuotes(tickers).then(q => setLiveQuotes(q)).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 30 * 1000);
    return () => clearInterval(iv);
  }, [signals]);

  // Summary stats — must be computed before filtered useMemo (avgConf is used inside it)
  const longCount = signals.filter(s => s.direction === 'LONG').length;
  const shortCount = signals.filter(s => s.direction === 'SHORT').length;
  const optionsCount = signals.filter(s => s.option != null).length;
  const avgConf = signals.length ? (signals.reduce((a, s) => a + s.confidence, 0) / signals.length) : 0;

  const filtered = useMemo(() => {
    let s = signals;
    if (activeFilter === 'long') s = s.filter(x => x.direction === 'LONG');
    else if (activeFilter === 'short') s = s.filter(x => x.direction === 'SHORT');
    else if (activeFilter === 'high') s = s.filter(x => x.confidence > avgConf);
    else if (activeFilter === 'options') s = s.filter(x => x.option != null);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      s = s.filter(x => x.ticker.toLowerCase().includes(q) || x.name.toLowerCase().includes(q) || x.category.toLowerCase().includes(q));
    }

    // Sort the visible list by calibrated confidence DESC, with raw score as
    // the tiebreaker so two equally-confident signals fall back to whichever
    // had more independent indicators aligned. This is the same order
    // loadSignals/loadMore use, so the ranking stays stable as new batches
    // arrive or as validation flips signals between pending → validated.
    s = [...s].sort((a, b) => {
      const conf = (b.confidence ?? 0) - (a.confidence ?? 0);
      if (conf !== 0) return conf;
      return (b.score ?? 0) - (a.score ?? 0);
    });
    return s;
  }, [signals, activeFilter, searchQuery, avgConf]);

  const filters = [
    { key: 'all', label: 'All Signals' },
    { key: 'long', label: 'Long' },
    { key: 'short', label: 'Short' },
    { key: 'high', label: 'High Confidence' },
    { key: 'options', label: 'Options Plays' },
  ];

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 'var(--r-md)',
              background: 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.06))',
              border: '1px solid rgba(245,158,11,0.25)',
            }}>
              <Zap size={18} color="var(--amber)" strokeWidth={2.4} />
            </span>
            Trade Signals
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 0 0' }}>
            Multi-factor scoring · cluster-decorrelated · calibrated against live track record
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            size="md"
            icon={Sparkles}
            loading={aiLoading}
            disabled={signals.length === 0}
            onClick={handleAiAnalysis}
          >
            {aiLoading ? 'Analyzing…' : Object.keys(aiInsights).length > 0 ? 'Refresh AI' : 'Get AI Analysis'}
          </Button>
          {/* Live scan state — visible during fetch, during validation,
              and once settled (so the user can always see how many
              signals were backtested). */}
          {(() => {
            if (refreshing) {
              return (
                <Badge tone="purple" size="md" icon={Loader} outlined>
                  <span style={{ marginLeft: 2 }}>Scanning…</span>
                </Badge>
              );
            }
            if (validateTotal > 0) {
              return (
                <Badge tone="amber" size="md" icon={Loader} outlined>
                  <span style={{ marginLeft: 2 }}>Validating {validateDone}/{validateTotal}</span>
                </Badge>
              );
            }
            const backtested = signals.filter(s => s._confidenceSource === 'backtest').length;
            if (backtested > 0) {
              return (
                <Badge tone="green" size="md" outlined>
                  <span style={{ marginLeft: 2 }}>{backtested} backtested</span>
                </Badge>
              );
            }
            return null;
          })()}
          <Button
            variant="ghost"
            size="md"
            icon={RefreshCw}
            onClick={() => loadSignals(true)}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="stat-row" style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {signals.length === 0 && refreshing
          ? Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="card" style={{ padding: '12px 16px', minWidth: 110 }}>
                <div className="skeleton" style={{ height: 10, width: 70, borderRadius: 3, marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 22, width: 44, borderRadius: 4 }} />
              </div>
            ))
          : [
              { label: 'Signals',   raw: signals.length, decimals: 0, suffix: '',  color: 'var(--accent-soft)', tint: 'rgba(91,106,240,0.06)',  border: 'rgba(91,106,240,0.20)', tip: 'Total signals that survived every quality gate this scan — confidence floor, regime gate, R:R floor, earnings blackout, etc.' },
              { label: 'Long',      raw: longCount,      decimals: 0, suffix: '',  color: 'var(--green)',       tint: 'var(--green-dim)',       border: 'rgba(34,197,94,0.20)', tip: 'Signals expecting price to rise. Open by buying shares (or CALL options).' },
              { label: 'Short',     raw: shortCount,     decimals: 0, suffix: '',  color: 'var(--red)',         tint: 'var(--red-dim)',         border: 'rgba(239,68,68,0.20)', tip: 'Signals expecting price to fall. Open by short-selling shares (or buying PUT options).' },
              { label: 'Options',   raw: optionsCount,   decimals: 0, suffix: '',  color: '#c4b5fd',            tint: 'var(--purple-dim)',      border: 'rgba(168,85,247,0.20)', tip: 'Signals enriched with a real Polygon options play — best near-ATM contract with live premium, Greeks, and expiry.' },
              { label: 'Avg conf.', raw: avgConf,        decimals: 0, suffix: '%', color: 'var(--amber)',       tint: 'var(--amber-dim)',       border: 'rgba(245,158,11,0.20)', tip: 'Average displayed confidence across all signals shown. Useful as a "is the market giving us strong setups today" sniff test.' },
            ].map((s, i) => (
              <div key={s.label} title={s.tip} className="animate-fade-in stagger" style={{
                background: s.tint, borderRadius: 'var(--r-md)', padding: '10px 16px',
                border: `1px solid ${s.border}`, minWidth: 110, flex: '1 1 0',
                '--i': i, cursor: 'help',
              }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{s.label}</div>
                <div className="mono" style={{ fontSize: 22, fontWeight: 800, color: s.color, letterSpacing: '-0.4px', lineHeight: 1 }}>
                  <CountUp end={s.raw} decimals={s.decimals} suffix={s.suffix} duration={700} />
                </div>
              </div>
            ))
        }
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search ticker, name, or category..."
          style={{
            padding: '10px 14px 10px 38px', borderRadius: 'var(--r-md)', width: '100%',
            border: '1px solid var(--border)', background: 'var(--bg-card)',
            color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Filters — pill geometry matching SignalCard badge system */}
      <div className="filter-row" style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {filters.map(f => {
          const active = activeFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              style={{
                padding: '7px 14px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
                background: active ? 'var(--grad-accent)' : 'transparent',
                color: active ? '#fff' : 'var(--text-secondary)',
                border: active ? 'none' : '1px solid var(--border)',
                boxShadow: active ? '0 4px 14px rgba(91,106,240,0.30)' : 'none',
                transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)',
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Error + progress banners — visible above everything when relevant.
          The progress bar covers both the slow generateTradeSignals fetch
          (5-15s, time-based estimate) and the validateSignalsBatch phase
          (real done/total). loadError surfaces hard failures so the user
          knows whether to retry or wait for the next auto-refresh. */}
      <ErrorBanner message={loadError} onDismiss={() => setLoadError(null)} />
      <ProgressBanner
        phase={refreshing ? 'fetch' : (validateTotal > 0 ? 'validate' : null)}
        elapsedMs={loadingElapsedMs}
        validateDone={validateDone}
        validateTotal={validateTotal}
      />

      {/* AI summary banner */}
      {aiError && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 10,
          background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <AlertTriangle size={14} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: '#ef4444', fontWeight: 600, marginBottom: 2 }}>
              AI analysis unavailable
            </div>
            <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5, wordBreak: 'break-word' }}>
              {aiError}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Mechanical signals (above) are unaffected — only the AI overlay is missing.
            </div>
          </div>
        </div>
      )}
      {aiSummary && !aiError && (
        <div style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 10,
          background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.18)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Sparkles size={12} color="#818cf8" />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#818cf8', letterSpacing: '0.05em' }}>AI MARKET ANALYSIS</span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#94a3b8', lineHeight: 1.65 }}>{aiSummary}</p>
        </div>
      )}

      {/* Signals grid */}
      <div className="signal-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
        {signals.length === 0 && refreshing
          ? Array.from({ length: 8 }, (_, i) => (
              <div key={i} style={{ background: 'rgba(15,23,42,0.6)', borderRadius: 12, padding: '16px 18px', border: '1px solid rgba(51,65,85,0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div className="skeleton" style={{ height: 16, width: 70, borderRadius: 4 }} />
                  <div className="skeleton" style={{ height: 16, width: 50, borderRadius: 4 }} />
                </div>
                <div className="skeleton skeleton-text" style={{ width: '90%' }} />
                <div className="skeleton skeleton-text" style={{ width: '70%' }} />
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <div className="skeleton" style={{ height: 22, width: 60, borderRadius: 6 }} />
                  <div className="skeleton" style={{ height: 22, width: 80, borderRadius: 6 }} />
                </div>
              </div>
            ))
          : filtered.map(sig => (
              // Per-card ErrorBoundary — a malformed field on one signal (e.g.
              // missing option chain, NaN target) shouldn't blank the grid.
              <ErrorBoundary key={sig.ticker} routeLabel={`signal-card:${sig.ticker}`}>
                <SignalCard signal={sig} cs={config.currencySymbol} onClick={() => setSelectedTicker(sig.ticker)}
                  livePrice={liveQuotes[sig.ticker]?.price}
                  onTrade={(tradeData) => openTrade(tradeData)}
                  aiInsight={aiInsights[sig.ticker]}
                  aiLoading={aiLoading} />
              </ErrorBoundary>
            ))
        }
      </div>

      {filtered.length === 0 && signals.length > 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#475569' }}>
          <Search size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 16, fontWeight: 600 }}>No signals match your filters</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Try adjusting your search or filter criteria</div>
        </div>
      )}

      {/* Zero-result empty state — scan completed cleanly but nothing passed the
          quality gates (low momentum session, heavy earnings blackout, etc.).
          Distinct from a hard failure (which renders the red error banner).
          Shows the per-gate filter breakdown so you can see *which* gate
          caught every candidate. */}
      {signals.length === 0 && !refreshing && !loadError && (
        <FilterBreakdownEmpty stats={filterStats} onRetry={() => loadSignals(true)} />
      )}

      {/* Always-visible footer with the per-scan filter breakdown — useful even
          when signals do ship, to verify the gates aren't over-aggressive. */}
      {signals.length > 0 && filterStats && <ScanFootnote stats={filterStats} />}

      {signals.length > 0 && !refreshing && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '10px 28px',
              borderRadius: 8, border: '1px solid rgba(99,102,241,0.3)',
              background: 'rgba(99,102,241,0.08)', color: '#818cf8',
              fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
              cursor: loadingMore ? 'wait' : 'pointer',
              opacity: loadingMore ? 0.6 : 1,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { if (!loadingMore) e.currentTarget.style.background = 'rgba(99,102,241,0.16)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
          >
            <RefreshCw size={13} style={{ animation: loadingMore ? 'spin 1s linear infinite' : 'none' }} />
            {loadingMore ? 'Loading…' : 'Load More Signals'}
          </button>
        </div>
      )}
    </div>
  );
}
